import { REFERENCE_SERIES_COLOR, SECONDARY_SHADE_STEP, darken } from '@/lib/chart-config';
import { cn } from '@/lib/utils';
import ColorSwatchPicker from './ColorSwatchPicker';

// Marker shapes the renderer supports; unset → it auto-cycles through them.
const MARKER_SYMBOLS: { id: string; glyph: string; label: string }[] = [
    { id: 'circle', glyph: '●', label: 'Círculo' },
    { id: 'square', glyph: '■', label: 'Quadrado' },
    { id: 'diamond', glyph: '◆', label: 'Losango' },
    { id: 'triangle', glyph: '▲', label: 'Triângulo' },
];

interface SeriesColorListProps {
    /** True for bar/line/radar/scatter (one color per series); false for value charts. */
    multiSeries: boolean;
    /** All source columns, used to resolve a column id to its label. */
    columns: { id: string; label: string }[];
    /** Selected Y-column ids, in series order (orderedYAxisColumnIds). */
    selectedColumnIds: string[];
    /** columnId → chosen hex; absent means "use the default". */
    seriesColors: Record<string, string>;
    /** Preview brand color used as the default for the first/only series. */
    primaryColor: string;
    /** Preview brand color used as the default for the second series (and darker beyond). */
    secondaryColor: string;
    /** Columns auto-detected as norm/reference context (gray, dashed, behind). */
    referenceColumnIds: string[];
    /** True for line/scatter: marks carry a per-series marker shape. */
    symbolsSupported?: boolean;
    /** columnId → chosen shape; absent means "auto" (the renderer cycles shapes). */
    seriesSymbols?: Record<string, string>;
    disabled?: boolean;
    onChange: (columnId: string, color: string) => void;
    /** Empty string clears the pick back to "auto". */
    onSymbolChange?: (columnId: string, symbol: string) => void;
}

/**
 * Per-series color pickers. Multi-series charts show one labeled swatch per
 * selected Y column; value charts (gauge/bullet/xy/distribution) show a single
 * "Cor do gráfico" swatch. Each defaults to the palette color it would render as
 * and only persists a color the user actually picks — an unset series stays on
 * the report's brand/palette colors.
 */
export default function SeriesColorList({
    multiSeries,
    columns,
    selectedColumnIds,
    seriesColors,
    primaryColor,
    secondaryColor,
    referenceColumnIds,
    symbolsSupported = false,
    seriesSymbols = {},
    disabled = false,
    onChange,
    onSymbolChange,
}: SeriesColorListProps) {
    if (selectedColumnIds.length === 0) return null;

    // The user's chosen brand colors, offered as the first swatches in every picker.
    const brandColors = [primaryColor, secondaryColor];
    const labelFor = (id: string) => columns.find((c) => c.id === id)?.label ?? 'Série';
    const isReference = (id: string) => referenceColumnIds.includes(id);
    const defaultColorFor = (id: string, index: number) => {
        if (isReference(id)) return REFERENCE_SERIES_COLOR;
        if (index === 0) return primaryColor;
        if (index === 1) return secondaryColor;
        return darken(secondaryColor, SECONDARY_SHADE_STEP * (index - 1));
    };

    if (!multiSeries) {
        const id = selectedColumnIds[0];
        return (
            <div>
                <label className="mb-2 block text-sm font-medium text-foreground">
                    Cor do gráfico
                </label>
                <ColorSwatchPicker
                    value={seriesColors[id] ?? ''}
                    defaultColor={primaryColor}
                    brandColors={brandColors}
                    onChange={(color) => onChange(id, color)}
                    disabled={disabled}
                    ariaLabel="Cor do gráfico"
                />
            </div>
        );
    }

    return (
        <div>
            <label className="mb-2 block text-sm font-medium text-foreground">
                Cores das séries
            </label>
            <div className="space-y-1.5">
                {selectedColumnIds.map((id, index) => (
                    <div
                        key={`color-${id}`}
                        className="flex items-center justify-between gap-2 rounded-lg border border-border px-3 py-1.5 text-sm"
                    >
                        <span className="truncate">{labelFor(id)}</span>
                        <div className="flex items-center gap-1.5">
                            {symbolsSupported && onSymbolChange && (
                                <div className="flex items-center gap-0.5">
                                    {MARKER_SYMBOLS.map((symbol) => {
                                        const active = seriesSymbols[id] === symbol.id;
                                        return (
                                            <button
                                                key={symbol.id}
                                                type="button"
                                                disabled={disabled}
                                                aria-pressed={active}
                                                title={
                                                    active
                                                        ? `${symbol.label} (clique para voltar ao automático)`
                                                        : `Marcador: ${symbol.label}`
                                                }
                                                // Re-clicking the active shape returns to "auto".
                                                onClick={() =>
                                                    onSymbolChange(id, active ? '' : symbol.id)
                                                }
                                                className={cn(
                                                    'flex size-6 items-center justify-center rounded border text-[10px] leading-none transition-colors disabled:cursor-not-allowed disabled:opacity-50',
                                                    active
                                                        ? 'border-foreground/40 bg-muted text-foreground'
                                                        : 'border-transparent text-muted-foreground/60 hover:border-border hover:text-foreground',
                                                )}
                                            >
                                                {symbol.glyph}
                                            </button>
                                        );
                                    })}
                                </div>
                            )}
                            <ColorSwatchPicker
                                value={seriesColors[id] ?? ''}
                                defaultColor={defaultColorFor(id, index)}
                                brandColors={brandColors}
                                onChange={(color) => onChange(id, color)}
                                disabled={disabled}
                                ariaLabel={`Cor de ${labelFor(id)}`}
                            />
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}
